import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ContactModal from './ContactModal';

const Nav = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location]);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    const menuLinks = [
        { to: '/work', label: 'Work' },
        { to: '/about', label: 'About' },
        { to: '/programs', label: 'Programs' },
        { to: '/storefront', label: 'Storefront' },
        { to: '/stack', label: 'Tech Stack' },
    ];

    return (
        <>
            <header className={`sticky top-0 z-50 w-full transition-all duration-200 ${scrolled ? 'bg-cream/95 backdrop-blur-md border-b border-sand' : 'bg-cream border-b border-sand'}`}>
                <nav className="mx-auto max-w-6xl px-6 py-5 grid grid-cols-3 items-center">
                    {/* Left - Contact */}
                    <div className="flex items-center">
                        <button
                            onClick={() => setContactOpen(true)}
                            className="text-xs tracking-wide text-ink hover:text-muted transition-colors"
                        >
                            + Contact
                        </button>
                    </div>

                    {/* Center - Logo */}
                    <Link to="/" className="justify-self-center hover:opacity-80 transition-opacity">
                        <span
                            className="text-lg md:text-xl text-ink tracking-[0.15em]"
                            style={{ fontFamily: "'Cinzel', serif" }}
                        >
                            AMANDA FILOMENA
                        </span>
                    </Link>

                    {/* Right - About + Menu */}
                    <div className="flex items-center justify-end gap-6">
                        <Link
                            to="/about"
                            className="hidden md:block text-xs tracking-wide text-ink hover:text-muted transition-colors"
                        >
                            About
                        </Link>
                        <button
                            className="flex items-center gap-2 text-ink hover:text-muted transition-colors"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Toggle menu"
                        >
                            <div className="flex flex-col gap-[5px]">
                                <span className={`block w-[18px] h-[1.5px] bg-current transition-transform duration-300 ${menuOpen ? 'rotate-45 translate-y-[3.25px]' : ''}`} />
                                <span className={`block w-[18px] h-[1.5px] bg-current transition-transform duration-300 ${menuOpen ? '-rotate-45 -translate-y-[3.25px]' : ''}`} />
                            </div>
                            <span className="text-xs tracking-wide">Menu</span>
                        </button>
                    </div>
                </nav>
            </header>

            {/* Right Sidebar Menu */}
            <div
                className={`fixed inset-0 z-[100] transition-opacity duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
                    onClick={() => setMenuOpen(false)}
                />

                {/* Sidebar */}
                <div
                    className={`absolute top-0 right-0 h-full w-full max-w-sm bg-cream border-l border-sand shadow-2xl transition-transform duration-300 ease-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                >
                    {/* Sidebar Header */}
                    <div className="flex items-center justify-between p-6 border-b border-sand">
                        <span
                            className="text-sm text-ink tracking-[0.15em]"
                            style={{ fontFamily: "'Cinzel', serif" }}
                        >
                            MENU
                        </span>
                        <button
                            onClick={() => setMenuOpen(false)}
                            className="text-muted hover:text-ink transition-colors p-1"
                            aria-label="Close menu"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Sidebar Links */}
                    <nav className="p-6 flex flex-col gap-2">
                        {menuLinks.map(({ to, label }) => (
                            <Link
                                key={to}
                                to={to}
                                className={`block py-3 text-lg tracking-wide transition-colors border-b border-sand/50 ${
                                    location.pathname === to ? 'text-terra' : 'text-ink hover:text-terra'
                                }`}
                                style={{ fontFamily: "'Cinzel', serif" }}
                            >
                                {label}
                            </Link>
                        ))}
                    </nav>

                    {/* Sidebar Footer */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-sand">
                        <p className="text-xs text-muted mb-4 tracking-wide">Follow along</p>
                        <div className="flex gap-4">
                            <a
                                href="https://www.instagram.com/amandafilomena_/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs tracking-wide text-ink hover:text-terra transition-colors"
                            >
                                Instagram
                            </a>
                            <a
                                href="https://www.linkedin.com/in/amanda-fc-anderson/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs tracking-wide text-ink hover:text-terra transition-colors"
                            >
                                LinkedIn
                            </a>
                            <a
                                href="https://github.com/amandafc-BYU"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs tracking-wide text-ink hover:text-terra transition-colors"
                            >
                                GitHub
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Modal */}
            <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
        </>
    );
};

export default Nav;
